import React, { useState, useEffect } from 'react'
import { apiGet, apiPost } from '../../utils/apiFetch'
import apiPath from "../../utils/pathObj";
import OButton from 'components/reusable/OButton'
import { useTranslation } from 'react-i18next'
import useToastContext from 'hooks/useToastContext'
import dayjs from 'dayjs'

const EmployeeBalances = () => {
  const { t } = useTranslation()
  const notification = useToastContext()
  const [employees, setEmployees] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [expandedEmployee, setExpandedEmployee] = useState(null)
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [adjustData, setAdjustData] = useState({
    employeeId: '',
    policyId: '',
    adjustmentType: 'add',
    days: 1,
    reason: ''
  })
  const [policies, setPolicies] = useState([])

  const getBalances = async () => {
    try {
      setIsLoading(true)
      const params = { year: dayjs().year() }
      if (searchTerm) params.keyword = searchTerm
      const res = await apiGet(apiPath.employeeLeaveBalances, params)
      setEmployees(res?.data?.results || [])
    } catch (error) {
      console.log('Error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getPolicies = async () => {
    try {
      const res = await apiGet(apiPath.leavePolicyList, {})
      setPolicies(res?.data?.results || [])
    } catch (error) {
      console.log('Error fetching policies:', error)
    }
  }

  useEffect(() => {
    getBalances()
    getPolicies()
  }, [])

  const handleSearch = () => {
    getBalances()
  }

  const handleAdjustBalance = async () => {
    if (!adjustData.reason) {
      notification.error(t('PLEASE_ENTER_REASON'))
      return
    }

    try {
      const res = await apiPost(apiPath.adjustLeaveBalance, {
        ...adjustData,
        days: Number(adjustData.days)
      })

      if (res?.data?.success) {
        notification.success(res?.data?.message)
        setShowAdjustModal(false)
        getBalances()
      } else {
        notification.error(res?.data?.message)
      }
    } catch (error) {
      notification.error(t('SOMETHING_WENT_WRONG'))
    }
  }

  const handleRunAccrual = async () => {
    try {
      setIsLoading(true)
      const res = await apiPost(apiPath.runAccrual, {})
      if (res?.data?.success) {
        notification.success(res?.data?.message + ` (${res?.data?.results?.updatedCount || 0} updated)`)
        getBalances()
      } else {
        notification.error(res?.data?.message)
      }
    } catch (error) {
      notification.error(t('SOMETHING_WENT_WRONG'))
    } finally {
      setIsLoading(false)
    }
  }

  const getUsagePercentage = (used, total) => {
    if (!total || total === 0) return 0
    return Math.min(100, Math.round((used / total) * 100))
  }

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500'
    if (percentage >= 50) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  return (
    <div className='bg-[#F9F9F9]'>
      <div className='bg-white border border-[#E9EDF9] rounded-lg'>
        {/* Header */}
        <div className='p-4 border-b flex items-center justify-between flex-wrap gap-3'>
          <h3 className='font-bold text-lg text-gray-800'>{t('EMPLOYEE_LEAVE_BALANCES')}</h3>
          <div className='flex gap-2'>
            <OButton
              label={t('RUN_ACCRUAL')}
              onClick={handleRunAccrual}
              type='button'
              style={{ borderRadius: '0.5rem', backgroundColor: '#10B981' }}
            />
          </div>
        </div>

        {/* Search */}
        <div className='p-4 border-b flex gap-3'>
          <input
            type='text'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={t('SEARCH_EMPLOYEE')}
            className='flex-1 p-2 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
          />
          <OButton label={t('O_SEARCH')} onClick={handleSearch} style={{ borderRadius: '0.5rem' }} />
        </div>

        {/* Employee List */}
        <div className='p-3'>
          {isLoading ? (
            <div className='flex justify-center py-12'>
              <div className='loading-spinner h-8 w-8 border-2 border-[#4F46E5]/30 border-t-[#4F46E5] rounded-full animate-spin'></div>
            </div>
          ) : employees.length === 0 ? (
            <div className='text-center py-12 text-gray-400'>{t('NO_EMPLOYEES_FOUND')}</div>
          ) : (
            <div className='space-y-3'>
              {employees.map((emp) => (
                <div key={emp._id} className='border rounded-lg overflow-hidden'>
                  {/* Employee Header */}
                  <div
                    className='p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50'
                    onClick={() => setExpandedEmployee(expandedEmployee === emp._id ? null : emp._id)}
                  >
                    <div className='flex items-center gap-4'>
                      <div className='w-10 h-10 rounded-full bg-[#4F46E5] text-white flex items-center justify-center font-bold'>
                        {emp.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className='font-medium text-gray-900'>{emp.name}</div>
                        <div className='text-xs text-gray-400'>{emp.employeeId}</div>
                      </div>
                    </div>
                    <div className='flex items-center gap-6'>
                      <div className='text-center'>
                        <div className='text-xs text-gray-400'>{t('AVAILABLE')}</div>
                        <div className='font-bold text-green-600'>{emp.totalAvailable} days</div>
                      </div>
                      <div className='text-center'>
                        <div className='text-xs text-gray-400'>{t('USED')}</div>
                        <div className='font-bold text-red-600'>{emp.totalUsed} days</div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setAdjustData({
                            ...adjustData,
                            employeeId: emp._id,
                            policyId: emp.balances?.[0]?.policyId || ''
                          })
                          setShowAdjustModal(true)
                        }}
                        className='px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100'
                      >
                        {t('ADJUST')}
                      </button>
                      <span className={`transform transition-transform ${expandedEmployee === emp._id ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </div>

                  {/* Expanded Balance Details */}
                  {expandedEmployee === emp._id && (
                    <div className='border-t p-4 bg-gray-50'>
                      <div className='grid gap-3'>
                        {emp.balances?.map((balance) => {
                          const percentage = getUsagePercentage(balance.usedDays, balance.totalAllotted)
                          return (
                            <div key={balance.policyId} className='bg-white p-3 rounded-lg border'>
                              <div className='flex justify-between items-center mb-2'>
                                <div>
                                  <span className='font-medium text-gray-900'>{balance.policyName}</span>
                                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                                    balance.isPaid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {balance.isPaid ? t('PAID') : t('UNPAID')}
                                  </span>
                                </div>
                                <span className='text-sm text-gray-500'>
                                  {balance.availableDays} / {balance.totalAllotted} days
                                </span>
                              </div>
                              {/* Progress Bar */}
                              <div className='w-full bg-gray-200 rounded-full h-2'>
                                <div
                                  className={`h-2 rounded-full transition-all ${getProgressColor(percentage)}`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <div className='flex justify-between mt-1 text-xs text-gray-400'>
                                <span>{t('USED')}: {balance.usedDays} days</span>
                                <span>{percentage}%</span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Adjust Balance Modal */}
      {showAdjustModal && (
        <>
          <div className='fixed inset-0 z-[60] bg-black opacity-40' onClick={() => setShowAdjustModal(false)} />
          <div className='fixed inset-0 z-[70] flex items-center justify-center'>
            <div className='bg-white rounded-xl shadow-xl w-full max-w-md'>
              <div className='p-4 border-b flex items-center justify-between'>
                <h3 className='font-bold text-lg'>{t('ADJUST_BALANCE')}</h3>
                <button onClick={() => setShowAdjustModal(false)} className='text-2xl text-gray-400 hover:text-gray-600'>×</button>
              </div>
              <div className='p-5 space-y-4'>
                {/* Policy Selection */}
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>{t('LEAVE_TYPE')}</label>
                  <select
                    value={adjustData.policyId}
                    onChange={(e) => setAdjustData({ ...adjustData, policyId: e.target.value })}
                    className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                  >
                    {policies.map(p => (
                      <option key={p._id} value={p._id}>{p.name} ({p.leaveType})</option>
                    ))}
                  </select>
                </div>

                {/* Add/Deduct */}
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>{t('ADJUSTMENT_TYPE')}</label>
                  <select
                    value={adjustData.adjustmentType}
                    onChange={(e) => setAdjustData({ ...adjustData, adjustmentType: e.target.value })}
                    className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                  >
                    <option key='add' value='add'>{t('ADD_DAYS')}</option>
                    <option key='deduct' value='deduct'>{t('DEDUCT_DAYS')}</option>
                  </select>
                </div>

                {/* Days */}
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>{t('DAYS')}</label>
                  <input
                    type='number'
                    min='0.5'
                    step='0.5'
                    value={adjustData.days}
                    onChange={(e) => setAdjustData({ ...adjustData, days: e.target.value })}
                    className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>{t('REASON')} *</label>
                  <textarea
                    rows={3}
                    value={adjustData.reason}
                    onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                    className='w-full p-3 text-sm border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0'
                    placeholder={t('ENTER_REASON')}
                  />
                </div>
              </div>
              <div className='p-4 border-t flex justify-end gap-3'>
                <button onClick={() => setShowAdjustModal(false)} className='px-6 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300'>
                  {t('O_CANCEL')}
                </button>
                <button onClick={handleAdjustBalance} className='px-6 py-2 text-sm bg-[#4F46E5] text-white rounded-md hover:bg-[#4338CA]'>
                  {t('O_SAVE')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default EmployeeBalances